// src/components/feed/FeedItemCard.tsx
//
// Single feed item card. Renders kind-specifically:
//   • postcard      — hero image of the visited landmark + name
//   • photo         — user-posted image + optional caption
//   • saved_message — quoted AI message text + (optional) location
//   • shared_reaction — "X liked Y's post" + nested preview
//   • guide_save / tour_complete — fall through to generic
//
// Header is author-led (Instagram/LinkedIn convention): tap the
// avatar OR name to navigate to the author's profile. Owner sees a
// visibility menu (private/followers/public) on their own posts.
// Everyone sees a heart button to react.

import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  LockOutlined,
  GroupOutlined,
  PublicOutlined,
  PlaceOutlined,
  AutoAwesomeOutlined,
  PhotoCameraOutlined,
  StarOutlineOutlined,
  Favorite,
  FavoriteBorder,
  MoreHoriz,
  Check,
  Close,
  ChatBubbleOutline,
  ArrowForward,
  IosShare,
  LinkOff,
  SummarizeOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { useT } from '../../i18n/useT';
import { SessionRecapBody } from './SessionRecapBody';
import { FeedItem, FeedVisibility, FeedItemKind } from '../../feed/types';
import { useAuthStore } from '../../stores/authStore';
import { reactToFeedItem, unreactToFeedItem } from '../../feed/reactions';
import { useMyReaction } from '../../feed/useMyReaction';
import { updateFeedItemVisibility } from '../../feed/updateFeedItemVisibility';
import {
  shareFeedItem,
  setFeedItemShareEnabled,
} from '../../feed/setShareEnabled';
import {
  isVisibilityAllowed,
  isShareAllowed,
  capExplanation,
} from '../../feed/visibilityCap';
import { useResolvedImageUrl } from '../../feed/useResolvedImageUrl';
import { AnnotatedImage } from './AnnotatedImage';
import { usePublicProfile } from '../../profile/usePublicProfile';
import {
  cleanContentForDisplay,
  generateFollowUpMessage,
} from '../../utils/interactiveContent';

interface Props {
  item: FeedItem;
  /** When true, suppresses interactive owner-only chrome (kebab
   *  menu, visibility toggles, share action) and viewer-side
   *  controls (heart/reaction). Used by the public /post/{itemId}
   *  page so anonymous viewers see a clean read-only card. */
  readOnly?: boolean;
}

const visibilityIcon = (v: FeedVisibility): React.ReactElement => {
  switch (v) {
    case 'private':
      return <LockOutlined sx={{ fontSize: 14 }} />;
    case 'followers':
      return <GroupOutlined sx={{ fontSize: 14 }} />;
    case 'public':
      return <PublicOutlined sx={{ fontSize: 14 }} />;
  }
};

const kindIcon = (kind: FeedItemKind): React.ReactElement | null => {
  switch (kind) {
    case 'postcard':
      return <StarOutlineOutlined sx={{ fontSize: 14, mr: 0.25 }} />;
    case 'photo':
      return <PhotoCameraOutlined sx={{ fontSize: 14, mr: 0.25 }} />;
    case 'saved_message':
      return <AutoAwesomeOutlined sx={{ fontSize: 14, mr: 0.25 }} />;
    case 'shared_reaction':
      return <Favorite sx={{ fontSize: 14, mr: 0.25 }} />;
    case 'session_recap':
      return <SummarizeOutlined sx={{ fontSize: 14, mr: 0.25 }} />;
    default:
      return null;
  }
};

const formatTime = (ts: any): string => {
  if (!ts || typeof ts.toDate !== 'function') return '';
  const d = ts.toDate() as Date;
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days}d`;
  return d.toLocaleDateString();
};

const avatarColorFor = (seed: string): string => {
  const palette = [
    '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
    '#c2185b', '#0288d1', '#f57c00', '#00796b',
    '#5d4037', '#455a64',
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
};

// Path-style attribution. Mirrors the URL convention so a card
// reads as "@dlipats/guide/ai" — origin tab + generator. Today
// the generator is always 'ai' (refreshGuide / generateGuideCards
// / streamTourGuide all run AI), so we hard-suffix it. Once we
// have user-authored content we'll discriminate.
const sourcePathFor = (
  source: { kind: string } | undefined,
  kind: FeedItemKind
): string => {
  switch (source?.kind) {
    case 'guide_card':
      return '/guide/ai';
    case 'message':
      return '/chat/ai';
    case 'visit':
      return '/visited';
    case 'tour':
      return '/tour/ai';
    case 'manual':
      return '';
    default:
      // Fall back to kind-derived path when source is missing.
      if (kind === 'shared_reaction') return '/reaction';
      if (kind === 'postcard') return '/visited';
      return '';
  }
};

// Best-effort short location label. We only have lat/lng + an
// optional place_name on the snapshot. If a specific name is
// present, render that; otherwise show coordinates rounded to
// 2dp so we don't pretend to a precision we don't have. (City/
// country resolution would need server-side reverse geocoding —
// flagged for a separate round.)
const locationLabelFor = (
  loc: { lat?: number; lng?: number; place_name?: string } | undefined
): string | null => {
  if (!loc) return null;
  if (loc.place_name && loc.place_name.trim()) return loc.place_name.trim();
  if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
    return `${loc.lat.toFixed(2)}, ${loc.lng.toFixed(2)}`;
  }
  return null;
};

// Strip raw chat markup from saved_message bodies before display
// and tokenize for rich rendering.
//
// Two transforms applied in order:
//
//   1. cleanContentForDisplay() — removes FOLLOW_UP_QUESTIONS JSON
//      blocks (and any orphaned half-blocks).
//   2. Tokenize the cleaned text into a sequence of {text} and
//      {chip} tokens so the renderer can produce inline clickable
//      chips for entity references like "[Berliner Dom](place:berliner-dom)".
//
// The raw form stays on disk so we keep flexibility — chips here
// could become richer later (icons, deep-links to entity pages).
type ChatChipToken = {
  type: 'chip';
  text: string;
  category: string;
  identifier: string;
  originalText: string;
};
type ChatTextToken = { type: 'text'; value: string };
type ChatToken = ChatTextToken | ChatChipToken;

const CHIP_REGEX = /\[([^\]]+)\]\(([^:)]+):([^)]+)\)/g;

const tokenizeSavedBody = (raw: string | undefined): ChatToken[] => {
  if (!raw) return [];
  const cleaned = cleanContentForDisplay(raw).trim();
  if (!cleaned) return [];

  const tokens: ChatToken[] = [];
  let lastIndex = 0;
  // Reset before iterating — module-level regex with /g keeps state
  // across calls otherwise.
  CHIP_REGEX.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = CHIP_REGEX.exec(cleaned)) !== null) {
    if (m.index > lastIndex) {
      tokens.push({ type: 'text', value: cleaned.slice(lastIndex, m.index) });
    }
    tokens.push({
      type: 'chip',
      text: m[1],
      category: m[2],
      identifier: m[3],
      originalText: m[0],
    });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < cleaned.length) {
    tokens.push({ type: 'text', value: cleaned.slice(lastIndex) });
  }
  return tokens;
};

// Color palette mirrors the chat's InteractiveContent component so
// chips in the feed look like the chips in the conversation they
// were saved from.
const CHIP_COLOR: Record<string, string> = {
  person: '#7c3aed',
  place: '#059669',
  event: '#d97706',
  architecture: '#0284c7',
  concept: '#db2777',
  period: '#7c3aed',
};
const chipColorFor = (category: string): string =>
  CHIP_COLOR[category] || '#4b5563';

export const FeedItemCard: React.FC<Props> = ({ item, readOnly = false }) => {
  const t = useT();
  const navigate = useNavigate();
  const { user, openAuthModal } = useAuthStore();
  const { reaction } = useMyReaction(item.id);

  const isOwner = !!user && user.uid === item.owner_id;
  const visibilityLabel: Record<FeedVisibility, string> = {
    private: t('Private'),
    followers: t('Followers'),
    public: t('Public'),
  };
  const kindLabel: Partial<Record<FeedItemKind, string>> = {
    postcard: t('Postcard'),
    photo: t('Photo'),
    saved_message: t('Saved'),
    guide_save: t('Saved guide'),
    tour_complete: t('Tour completed'),
    shared_reaction: t('Liked'),
  };

  const { snapshot, author } = item;

  // Pull fresh public profile so the avatar/name reflect the latest
  // values even when the embedded author snapshot on this feed_item
  // is stale (e.g. user changed their photo after this was posted).
  // Falls back to the embedded snapshot if the profile is missing.
  const { profile: liveProfile } = usePublicProfile(item.owner_id);
  const displayName =
    liveProfile?.display_name ||
    author?.display_name ||
    t('Anonymous traveler');
  const photoUrl = liveProfile?.photo_url || author?.photo_url || undefined;
  const username = liveProfile?.username;
  const initial = (displayName || '?').trim().charAt(0).toUpperCase();

  // Resolve the snapshot's image — the field can be either a Firebase
  // Storage path (e.g. images/{uid}/temp_xxx/yyy) or already a URL.
  // Pass `item.id` so non-owner viewers (followers / public viewers
  // of share_enabled posts) route through the feedImageProxy Cloud
  // Function. Owners still get the fast direct getDownloadURL path.
  const resolvedImageUrl = useResolvedImageUrl(
    snapshot.preview_url || snapshot.image_path || null,
    item.id
  );

  const sourcePath = sourcePathFor(item.source, item.kind);
  const locationLabel = locationLabelFor(snapshot.location);

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const handleAvatarClick = () => {
    if (!item.owner_id) return;
    navigate(`/u/${encodeURIComponent(item.owner_id)}`);
  };

  const handleHeartClick = async () => {
    if (!user) return;
    if (user.isAnonymous) {
      openAuthModal('signup');
      return;
    }
    if (reaction) {
      await unreactToFeedItem(item.id);
    } else {
      await reactToFeedItem(item.id, 'heart');
    }
  };

  const handleVisibilitySelect = async (next: FeedVisibility) => {
    setMenuAnchor(null);
    if (next === item.visibility) return;
    await updateFeedItemVisibility(item.id, next);
  };

  // Sharing — toggles share_enabled and either opens the platform
  // share sheet or copies the public /post/{id} URL to clipboard.
  // The toast surfaces the outcome (no toast on native share — the
  // OS sheet IS the feedback). Errors are swallowed silently;
  // worst case the user re-taps Share. Share menu also exposes a
  // "Stop sharing" entry that flips the flag back off without
  // opening any sheet — used to revoke a link that's gone too far.
  const [shareToast, setShareToast] = useState<string | null>(null);
  const handleShare = async () => {
    setMenuAnchor(null);
    try {
      const titleHint =
        snapshot.title ||
        (item.kind === 'saved_message'
          ? 'Saved from chat'
          : 'Instant Tours');
      const result = await shareFeedItem({
        itemId: item.id,
        alreadyEnabled: !!item.share_enabled,
        title: titleHint,
        text: snapshot.caption || '',
      });
      if (result.method === 'clipboard') {
        setShareToast(t('Link copied'));
      } else if (result.method === 'unsupported') {
        // Last-ditch: show the URL in the toast so the user can
        // long-press to copy. Rare path — most modern browsers
        // support either Web Share or Clipboard.
        setShareToast(result.url);
      }
    } catch (err) {
      console.warn('share failed', err);
      setShareToast(t('Could not share. Please try again.'));
    }
  };
  const handleStopSharing = async () => {
    setMenuAnchor(null);
    try {
      await setFeedItemShareEnabled(item.id, false);
      setShareToast(t('Sharing turned off'));
    } catch (err) {
      console.warn('stop sharing failed', err);
    }
  };

  // Chat-CTA: viewer-aware navigation.
  //
  //   • Owner viewing their own saved_message → deep-link to the
  //     specific message in the source conversation:
  //     /chat/{convId}#message-{messageId}. VirtualizedMessageList
  //     reads the hash on mount, scrolls the message into view, and
  //     briefly highlights it. Falls through to plain /chat/{convId}
  //     when source.id is missing (legacy items pre-deep-link).
  //
  //   • Anyone else viewing a saved_message → "Start a new chat" —
  //     navigate to /chat with the freshChat flag so they land on
  //     the WelcomeScreen and can pick a mode + ask their own
  //     question. The saved card was a thought-starter; we don't
  //     drop them into the owner's private conversation.
  //
  // Only saved_message gets this CTA today. Other kinds (postcard,
  // guide_save, shared_reaction) don't have a natural "continue"
  // semantic; can layer on later if useful.
  const sourceConversationId = (item.source as any)?.conversation_id;
  const sourceMessageId = (item.source as any)?.id;
  const showChatCta =
    item.kind === 'saved_message' &&
    (isOwner ? !!sourceConversationId : true);
  const handleChatCta = () => {
    if (isOwner && sourceConversationId) {
      const hash = sourceMessageId ? `#message-${sourceMessageId}` : '';
      navigate(`/chat/${sourceConversationId}${hash}`);
    } else {
      navigate('/chat', { state: { freshChat: true } });
    }
  };

  // Chip click — viewer-aware, mirrors the CTA design:
  //
  //   • Owner: navigate into the source conversation with autoPrompt
  //     state. ChatInterface picks it up and sends the generated
  //     follow-up as a new user message in the existing chat — so
  //     tapping a chip from a saved card behaves identically to
  //     tapping the same chip live in the original chat.
  //
  //   • Non-owner: navigate to /chat with freshChat + autoPrompt.
  //     ChatRoute skips auto-resume; ChatInterface creates a new
  //     conversation and auto-sends the generated prompt. Each
  //     viewer forks their own thread from a saved chip without
  //     touching the owner's data.
  //
  // Generation uses generateFollowUpMessage() — the same helper the
  // live chat uses, so prompt phrasing is identical.
  const handleChipClick = (chip: ChatChipToken) => {
    const prompt = generateFollowUpMessage({
      text: chip.text,
      category: chip.category,
      identifier: chip.identifier,
      originalText: chip.originalText,
    });
    if (isOwner && sourceConversationId) {
      navigate(`/chat/${sourceConversationId}`, {
        state: { autoPrompt: prompt },
      });
    } else {
      navigate('/chat', {
        state: { freshChat: true, autoPrompt: prompt },
      });
    }
  };

  return (
    <Card
      sx={{
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          pt: 1.25,
          pb: 0.75,
          gap: 1,
        }}
      >
        <Avatar
          src={photoUrl}
          alt={displayName}
          onClick={handleAvatarClick}
          sx={{
            width: 32,
            height: 32,
            fontSize: 14,
            fontWeight: 600,
            bgcolor: photoUrl
              ? undefined
              : avatarColorFor(item.owner_id || 'anon'),
            color: 'common.white',
            cursor: 'pointer',
          }}
        >
          {initial}
        </Avatar>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            onClick={handleAvatarClick}
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            {displayName}
            {username && (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 400, ml: 0.5 }}
              >
                @{username}
                {sourcePath}
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {kindIcon(item.kind)}
            <Typography variant="caption" color="text.secondary">
              {kindLabel[item.kind] ?? item.kind}
              {' · '}
              {formatTime(item.created_at)}
            </Typography>
            {locationLabel && (
              <Chip
                icon={<PlaceOutlined sx={{ fontSize: 12 }} />}
                label={locationLabel}
                size="small"
                variant="outlined"
                sx={{
                  height: 18,
                  fontSize: 10,
                  '& .MuiChip-icon': { ml: 0.25, mr: -0.25 },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Box>
        </Box>
        <Chip
          icon={visibilityIcon(item.visibility)}
          label={visibilityLabel[item.visibility]}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            fontSize: 11,
            '& .MuiChip-icon': { ml: 0.5 },
          }}
        />
        {isOwner && !readOnly && (
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label={t('More')}
          >
            <MoreHoriz fontSize="small" />
          </IconButton>
        )}
        {isOwner && !readOnly && (
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {t('Visibility')}
              </Typography>
            </MenuItem>
            {(['private', 'followers', 'public'] as FeedVisibility[]).map(
              (v) => {
                const allowed = isVisibilityAllowed(
                  v,
                  item.source_visibility
                );
                const explanation = item.source_visibility
                  ? capExplanation(item.source_visibility)
                  : '';
                const row = (
                  <MenuItem
                    key={v}
                    onClick={
                      allowed ? () => handleVisibilitySelect(v) : undefined
                    }
                    selected={item.visibility === v}
                    disabled={!allowed}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                      }}
                    >
                      {visibilityIcon(v)}
                      <Typography variant="body2">
                        {visibilityLabel[v]}
                      </Typography>
                    </Box>
                    {item.visibility === v && (
                      <Check fontSize="small" sx={{ ml: 1 }} />
                    )}
                  </MenuItem>
                );
                // Tooltip only when capped; MUI tooltips on
                // disabled MenuItems need a wrapper span to
                // capture pointer events.
                if (!allowed && explanation) {
                  return (
                    <Tooltip
                      key={v}
                      title={t(explanation)}
                      placement="left"
                    >
                      <span>{row}</span>
                    </Tooltip>
                  );
                }
                return row;
              }
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {t('Share')}
              </Typography>
            </MenuItem>
            {(() => {
              const shareAllowed = isShareAllowed(item.source_visibility);
              const shareRow = (
                <MenuItem
                  onClick={shareAllowed ? handleShare : undefined}
                  disabled={!shareAllowed}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1,
                    }}
                  >
                    <IosShare sx={{ fontSize: 16 }} />
                    <Typography variant="body2">
                      {item.share_enabled
                        ? t('Share link')
                        : t('Share — anyone with link')}
                    </Typography>
                  </Box>
                </MenuItem>
              );
              if (!shareAllowed && item.source_visibility) {
                return (
                  <Tooltip
                    title={t(capExplanation(item.source_visibility))}
                    placement="left"
                  >
                    <span>{shareRow}</span>
                  </Tooltip>
                );
              }
              return shareRow;
            })()}
            {item.share_enabled && (
              <MenuItem onClick={handleStopSharing}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <LinkOff sx={{ fontSize: 16 }} />
                  <Typography variant="body2" color="error">
                    {t('Stop sharing')}
                  </Typography>
                </Box>
              </MenuItem>
            )}
          </Menu>
        )}
      </Box>

      {resolvedImageUrl && (
        <>
          <Box sx={{ position: 'relative' }}>
            <AnnotatedImage
              source={snapshot.image_path || snapshot.preview_url}
              alt={snapshot.title || ''}
              maxHeight={360}
              feedItemId={item.id}
              onImageClick={() => setLightboxOpen(true)}
            />
            {/* Photo-grouping badge — when this postcard groups
                multiple visits (a stop with sibling/revisit child
                photos), show "+N photos" so the user knows there's
                more in the lightbox. Phase 2 turns the lightbox
                into a swipeable carousel; for now the badge alone
                signals the grouping. */}
            {typeof item.photo_count === 'number' && item.photo_count > 1 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  backdropFilter: 'blur(8px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                }}
              >
                <PhotoCameraOutlined sx={{ fontSize: 12 }} />
                {`${item.photo_count}`}
              </Box>
            )}
          </Box>
          <Dialog
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            maxWidth="lg"
            fullWidth
            // Click anywhere on the backdrop to close — same as
            // tapping the close button. Common image-viewer affordance.
            PaperProps={{
              sx: {
                bgcolor: 'rgba(0,0,0,0.92)',
                boxShadow: 'none',
                m: { xs: 0, sm: 2 },
                maxHeight: { xs: '100vh', sm: '95vh' },
              },
            }}
          >
            <DialogContent
              onClick={() => setLightboxOpen(false)}
              sx={{
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                cursor: 'zoom-out',
                minHeight: { xs: '100vh', sm: 'auto' },
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxOpen(false);
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: 'common.white',
                  bgcolor: 'rgba(0,0,0,0.4)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                }}
                aria-label={t('Close')}
              >
                <Close />
              </IconButton>
              <AnnotatedImage
                source={snapshot.image_path || snapshot.preview_url}
                alt={snapshot.title || ''}
                mode="lightbox"
                feedItemId={item.id}
                onImageClick={() => setLightboxOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </>
      )}

      <Box sx={{ px: 1.5, py: 1.25 }}>
        {item.kind === 'session_recap' ? (
          /* Recap cards have their own body renderer — stat strip
             + prose + tappable "next time" hook. Replaces the
             default title/caption/body pattern entirely. */
          <SessionRecapBody
            item={item}
            onNextHook={(prompt) => {
              // Push the next-time prompt into chat as if the user
              // typed it. Same channel saved_message uses.
              handleChipClick({
                category: 'recap_hook',
                identifier: 'session_recap',
                text: prompt,
                originalText: prompt,
              } as any);
            }}
          />
        ) : (
          <>
        {snapshot.title && (
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {snapshot.title}
          </Typography>
        )}
        {snapshot.caption && (
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {snapshot.caption}
          </Typography>
        )}
        {item.kind === 'saved_message' &&
          snapshot.body &&
          (() => {
            const tokens = tokenizeSavedBody(snapshot.body);
            if (tokens.length === 0) return null;
            return (
              <Typography
                variant="body2"
                component="div"
                sx={{
                  mt: 0.5,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                  pl: 1.25,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {tokens.map((tok, i) => {
                  if (tok.type === 'text') {
                    return <span key={i}>{tok.value}</span>;
                  }
                  const color = chipColorFor(tok.category);
                  return (
                    <Box
                      key={i}
                      component="button"
                      type="button"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleChipClick(tok);
                      }}
                      sx={{
                        // Inline-chip styling — mirrors the live chat's
                        // InlineChip but tuned for the feed's smaller,
                        // italic body type.
                        display: 'inline',
                        verticalAlign: 'baseline',
                        font: 'inherit',
                        fontStyle: 'normal',
                        background: 'transparent',
                        border: 0,
                        padding: 0,
                        margin: 0,
                        color,
                        fontWeight: 600,
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted',
                        textDecorationColor: `${color}66`,
                        textUnderlineOffset: '2px',
                        cursor: 'pointer',
                        '&:hover': {
                          textDecorationStyle: 'solid',
                          textDecorationColor: color,
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${color}`,
                          outlineOffset: 2,
                          borderRadius: 2,
                        },
                      }}
                    >
                      {tok.text}
                    </Box>
                  );
                })}
              </Typography>
            );
          })()}
        {snapshot.location && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 1,
              color: 'text.secondary',
            }}
          >
            <PlaceOutlined sx={{ fontSize: 14 }} />
            <Typography variant="caption">
              {snapshot.location.place_name ||
                snapshot.location.address ||
                (typeof snapshot.location.lat === 'number' &&
                typeof snapshot.location.lng === 'number'
                  ? `${snapshot.location.lat.toFixed(4)}, ${snapshot.location.lng.toFixed(4)}`
                  : '')}
            </Typography>
          </Box>
        )}
          </>
        )}
      </Box>

      {showChatCta && (
        <Box
          sx={{
            px: 1.5,
            pb: 1.25,
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            size="small"
            startIcon={<ChatBubbleOutline sx={{ fontSize: 16 }} />}
            endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
            onClick={handleChatCta}
            sx={{
              justifyContent: 'space-between',
              textTransform: 'none',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
            }}
          >
            {isOwner
              ? t('Continue this conversation')
              : t('Start your own chat')}
          </Button>
        </Box>
      )}

      {!readOnly && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            pb: 1,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <IconButton
            size="small"
            onClick={handleHeartClick}
            sx={{ color: reaction ? 'error.main' : 'text.secondary' }}
            aria-label={reaction ? t('Unlike') : t('Like')}
          >
            {reaction ? (
              <Favorite fontSize="small" />
            ) : (
              <FavoriteBorder fontSize="small" />
            )}
          </IconButton>
          {item.reactions_count > 0 && (
            <Typography variant="caption" color="text.secondary">
              {item.reactions_count}
            </Typography>
          )}
        </Box>
      )}

      {/* Share-action toast. Auto-dismisses; sits at the bottom-
          center (theme default) so it doesn't fight with feed
          scroll. */}
      <Snackbar
        open={!!shareToast}
        autoHideDuration={3000}
        onClose={() => setShareToast(null)}
        message={shareToast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Card>
  );
};
